import React from 'react';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import {
  REPRESENTATIVE_EVIDENCE_NOTE,
  REPRESENTATIVE_EVIDENCE_PACKAGE_FALLBACK,
  sanitizeExternalLabel,
} from '../../domain/externalReview/externalReportLanguage';
import { api } from '../../services/api';

type EvidenceItem = {
  id?: string | null;
  kind?: string | null;
  title?: string | null;
  media_uri?: string | null;
  thumbnail_uri?: string | null;
  area_label?: string | null;
  rule_label?: string | null;
  captured_at?: string | null;
};

type Props = {
  representativeEvidence?: Record<string, unknown> | null;
  isDemoOrLoopedSource?: boolean;
  alwaysShow?: boolean;
};

function resolveMediaUrl(uri?: string | null): string | null {
  const raw = String(uri || '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = String(api.defaults.baseURL || window.location.origin).replace(/\/$/, '');
  return `${base}${raw.startsWith('/') ? raw : `/${raw}`}`;
}

function formatKindLabel(kind?: string | null): string {
  const normalized = String(kind || '').trim().toLowerCase();
  if (normalized === 'clip') return 'Representative clip';
  return 'Representative snapshot';
}

export default function ExternalRepresentativeEvidenceSection({
  representativeEvidence,
  isDemoOrLoopedSource,
  alwaysShow = false,
}: Props) {
  const items = Array.isArray(representativeEvidence?.items)
    ? (representativeEvidence?.items as EvidenceItem[])
    : [];
  const visibleItems = items
    .filter((item) => item && (item.media_uri || item.thumbnail_uri))
    .slice(0, 4);
  const packageFallback = String(
    representativeEvidence?.package_fallback || REPRESENTATIVE_EVIDENCE_PACKAGE_FALLBACK,
  );

  if (!visibleItems.length && !alwaysShow) return null;

  return (
    <Card variant="outlined" data-testid="external-representative-evidence">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Representative evidence
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {String(representativeEvidence?.description || REPRESENTATIVE_EVIDENCE_NOTE)}
        </Typography>
        {visibleItems.length > 0 ? (
          <Grid container spacing={2}>
            {visibleItems.map((item) => {
              const mediaUrl = resolveMediaUrl(item.thumbnail_uri || item.media_uri);
              const title = sanitizeExternalLabel(
                item.title || formatKindLabel(item.kind),
                { isDemo: isDemoOrLoopedSource },
              );
              const area = sanitizeExternalLabel(item.area_label, { isDemo: isDemoOrLoopedSource });
              const rule = sanitizeExternalLabel(item.rule_label, { isDemo: isDemoOrLoopedSource });
              return (
                <Grid item xs={12} sm={6} key={String(item.id || mediaUrl || title)}>
                  <Stack spacing={1}>
                    {mediaUrl ? (
                      <Box
                        component="img"
                        src={mediaUrl}
                        alt={title}
                        sx={{
                          width: '100%',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          objectFit: 'cover',
                          maxHeight: 220,
                        }}
                      />
                    ) : null}
                    <Typography variant="subtitle2">{title}</Typography>
                    {area ? (
                      <Typography variant="body2" color="text.secondary">
                        Area: {area}
                      </Typography>
                    ) : null}
                    {rule ? (
                      <Typography variant="body2" color="text.secondary">
                        Condition: {rule}
                      </Typography>
                    ) : null}
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Typography variant="body2" data-testid="external-representative-evidence-fallback">
            {packageFallback}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
